import { DecimalSchema } from "../../../decimal";
import { MARGIN_MODE, ORDER_SIDE, ORDER_TYPE, PairSymbol } from "../../../shared";
import { z } from "zod";

const PositivePriceStringSchema = z
  .string()
  .min(1)
  .max(100, "Price string too long")
  .refine((v) => /^\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v), { error: "Price must be a valid positive numeric string" });

const HubTradeMetadataSchema = z
  .object({
    tradeAndSend: z.boolean().optional(),
    defiAddress: z.string().min(1).optional(),
  })
  .partial();

/**
 * Schema for the payload sent to `POST /v1/hub/trade/prepare-order`.
 * Keeping this in @cede/types lets the frontend and backend share the same contract.
 */
export const HubTradePrepareOrderBodySchema = z.object({
  accountId: z.number().int().positive(),
  pairSymbol: z.custom<PairSymbol>(
    (val) => {
      if (typeof val !== "string") return false;
      // Allow CEX format (BASE/QUOTE) and perpetual format (BASE/QUOTE:SETTLE)
      return /^[^/]+\/[^/]+(:[^:]+)?$/.test(val);
    },
    {
      error: "Invalid pair symbol format",
    },
  ),
  orderSide: z.nativeEnum(ORDER_SIDE),
  orderType: z.nativeEnum(ORDER_TYPE),
  amount: z
    .string()
    .min(1)
    .max(100, "Amount string too long")
    .refine((v) => /^-?\d+(\.\d+)?$/.test(v), { error: "Amount must be a valid numeric string" }),
  price: PositivePriceStringSchema,
  exchangeId: z.string().min(1).optional(),
  quoteAmount: z
    .string()
    .min(1)
    .max(100, "Quote amount string too long")
    .refine((v) => /^-?\d+(\.\d+)?$/.test(v), { error: "Quote amount must be a valid numeric string" })
    .optional(),
  leverage: z.number().int().positive().optional(),
  marginMode: z.nativeEnum(MARGIN_MODE).optional(),
  reduceOnly: z.boolean().optional(),
  stopLossPrice: PositivePriceStringSchema.optional(),
  takeProfitPrice: PositivePriceStringSchema.optional(),
});

export type HubTradePrepareOrderBody = z.infer<typeof HubTradePrepareOrderBodySchema>;

/**
 * Schema for the payload sent to `POST /v1/hub/trade/create-order`.
 */
export const HubTradeCreateOrderBodySchema = HubTradePrepareOrderBodySchema.extend({
  amount: DecimalSchema,
  price: DecimalSchema,
  campaignId: z.string().min(1),
  hteCampaignId: z.string().min(1).optional(),
  metadata: HubTradeMetadataSchema.optional(),
});

export type HubTradeCreateOrderBody = z.infer<typeof HubTradeCreateOrderBodySchema>;
